import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { useWebsiteData } from '../../../context/WebsiteDataContext';

const ProjectsShowcase = () => {
  const { getActiveProjects } = useWebsiteData();
  const projects = getActiveProjects();
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Get featured projects (limit to 6 for homepage)
  const featuredProjects = projects.slice(0, 6);

  const categories = [
    { id: 'all', name: 'All Projects', count: featuredProjects.length },
    { id: 'residential', name: 'Residential', count: featuredProjects.filter(p => p.category === 'residential').length },
    { id: 'commercial', name: 'Commercial', count: featuredProjects.filter(p => p.category === 'commercial').length },
    { id: 'industrial', name: 'Industrial', count: featuredProjects.filter(p => p.category === 'industrial').length },
    { id: 'interior', name: 'Interior', count: featuredProjects.filter(p => p.category === 'interior').length }
  ].filter(cat => cat.count > 0); // Only show categories with projects

  const filteredProjects = selectedCategory === 'all' 
    ? featuredProjects 
    : featuredProjects.filter(project => project.category === selectedCategory);

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'residential': return 'Home';
      case 'commercial': return 'Building';
      case 'industrial': return 'Factory';
      case 'interior': return 'Palette';
      default: return 'Building';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (featuredProjects.length === 0) {
    return null; // Don't render section if no projects
  }

  return (
    <section id="projects" className="py-16 bg-muted/20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="FolderOpen" size={16} />
            <span>Our Portfolio</span>
          </div>
          <h2 className="font-headline text-3xl md:text-4xl font-bold text-foreground mb-4">
            Featured Projects
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Explore our diverse portfolio of successful construction and design projects across Pune and Maharashtra
          </p>
        </div>

        {/* Category Filter */}
        {categories.length > 1 && (
          <div className="flex justify-center mb-8">
            <div className="flex space-x-2 bg-card rounded-lg p-1 border border-border">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    selectedCategory === category.id
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  {category.name}
                  <span className="ml-2 text-xs opacity-75">({category.count})</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {filteredProjects.map((project) => (
            <div key={project.id} className="bg-card rounded-lg border border-border overflow-hidden hover:shadow-lg transition-shadow duration-300 group">
              {/* Project Image */}
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4 flex space-x-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                    {project.status === 'completed' ? 'Completed' : 'In Progress'}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                    {project.category.charAt(0).toUpperCase() + project.category.slice(1)}
                  </span>
                </div>
                {project.rating && (
                  <div className="absolute top-4 right-4 flex items-center space-x-1 bg-white/90 rounded-full px-2 py-1">
                    <Icon name="Star" size={12} className="text-yellow-500 fill-current" />
                    <span className="text-xs font-medium">{project.rating}</span>
                  </div>
                )}
              </div>

              {/* Project Content */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-xl font-bold text-foreground line-clamp-2 group-hover:text-primary transition-colors">
                    {project.title}
                  </h3>
                  <Icon name={getCategoryIcon(project.category)} size={20} className="text-primary flex-shrink-0 ml-2" />
                </div>

                <p className="text-muted-foreground mb-4 line-clamp-2">{project.description}</p>

                {/* Project Details */}
                <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Location:</span>
                    <div className="font-medium">{project.location}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Area:</span>
                    <div className="font-medium">{project.area}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Duration:</span>
                    <div className="font-medium">{project.duration}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Budget:</span>
                    <div className="font-medium">{project.budget}</div>
                  </div>
                </div>

                {/* Services */}
                {project.services && project.services.length > 0 && (
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-1">
                      {project.services.slice(0, 3).map((service, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full"
                        >
                          {service}
                        </span>
                      ))}
                      {project.services.length > 3 && (
                        <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">
                          +{project.services.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Client Testimonial Preview */}
                {project.testimonial && (
                  <div className="mb-4 p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <Icon name="Quote" size={14} className="text-primary flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-muted-foreground italic line-clamp-2">
                        "{project.testimonial}"
                      </p>
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">
                      - {project.client}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex space-x-2">
                  <Link to={`/project/${project.id}`} className="flex-1">
                    <Button
                      variant="outline"
                      size="sm"
                      fullWidth
                      iconName="Eye"
                      iconPosition="left"
                    >
                      View Details
                    </Button>
                  </Link>
                  <Button
                    variant="default"
                    size="sm"
                    fullWidth
                    iconName="MessageSquare"
                    iconPosition="left"
                    onClick={() => {
                      const message = `Hi, I'm interested in a project similar to "${project.title}". Can you provide more information?`;
                      const whatsappUrl = `https://wa.me/918003989393?text=${encodeURIComponent(message)}`;
                      window.open(whatsappUrl, '_blank');
                    }}
                  >
                    Get Quote
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All Projects Button */}
        <div className="text-center">
          <Link to="/projects">
            <Button
              variant="outline"
              size="lg"
              iconName="ArrowRight"
              iconPosition="right"
              className="px-8"
            >
              View All Projects ({projects.length})
            </Button>
          </Link>
        </div>

        {/* Project Stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center p-6 bg-card rounded-lg border border-border">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Icon name="CheckCircle" size={24} className="text-primary" />
            </div>
            <div className="text-2xl font-bold text-foreground mb-1">
              {projects.filter(p => p.status === 'completed').length}+
            </div>
            <div className="text-sm text-muted-foreground">Completed Projects</div>
          </div>

          <div className="text-center p-6 bg-card rounded-lg border border-border">
            <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Icon name="MapPin" size={24} className="text-secondary" />
            </div>
            <div className="text-2xl font-bold text-foreground mb-1">15+</div>
            <div className="text-sm text-muted-foreground">Locations Served</div>
          </div>

          <div className="text-center p-6 bg-card rounded-lg border border-border">
            <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Icon name="Star" size={24} className="text-accent" />
            </div>
            <div className="text-2xl font-bold text-foreground mb-1">4.9/5</div>
            <div className="text-sm text-muted-foreground">Client Rating</div>
          </div>

          <div className="text-center p-6 bg-card rounded-lg border border-border">
            <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Icon name="TrendingUp" size={24} className="text-warning" />
            </div>
            <div className="text-2xl font-bold text-foreground mb-1">₹50Cr+</div>
            <div className="text-sm text-muted-foreground">Project Value</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectsShowcase;